import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { FaPhone, FaRupeeSign, FaMobileAlt, FaCheckCircle, FaShieldAlt } from 'react-icons/fa';
import toast from 'react-hot-toast';

const PhonePePayment = ({ amount, feeId, studentId, feeMonth, onSuccess, onCancel }) => {
  const [step, setStep] = useState('init');

  const handlePhonePePayment = async () => {
    try {
      setStep('processing');
      toast.loading('Processing PhonePe payment...');

      await new Promise(resolve => setTimeout(resolve, 2000));

      setStep('verifying');
      toast.loading('Verifying payment with PhonePe...');
      await new Promise(resolve => setTimeout(resolve, 1500));

      setStep('success');
      toast.dismiss();
      toast.success('Payment successful! ✅');
      
      if (onSuccess) {
        setTimeout(() => onSuccess({
          amount,
          feeId,
          studentId,
          feeMonth,
          transactionId: 'TXN-' + Date.now().toString().slice(-10)
        }), 1000);
      }
    } catch (error) {
      setStep('failed');
      toast.error('Payment failed. Please try again.');
    }
  };

  return (
    <motion.div
      initial={{ opacity: 0, y: 50 }}
      animate={{ opacity: 1, y: 0 }}
      className="bg-white rounded-2xl shadow-2xl p-6 max-w-md w-full mx-auto"
    >
      <div className="text-center mb-6">
        <motion.div
          animate={{ 
            scale: [1, 1.05, 1],
            rotate: [0, 5, -5, 0]
          }}
          transition={{ duration: 2, repeat: Infinity }}
          className="w-20 h-20 bg-gradient-to-r from-purple-600 to-blue-500 rounded-2xl flex items-center justify-center mx-auto shadow-lg"
        >
          <FaPhone className="text-white text-4xl" />
        </motion.div>
        <h3 className="text-2xl font-bold text-gray-800 mt-3">PhonePe</h3>
        <p className="text-sm text-gray-500">Secure & Instant Payment</p>
      </div>

      <motion.div
        initial={{ scale: 0.9 }}
        animate={{ scale: 1 }}
        className="bg-gradient-to-r from-purple-50 to-blue-50 rounded-xl p-4 mb-6 text-center border border-purple-100"
      >
        <p className="text-sm text-gray-500">Amount to Pay</p>
        <p className="text-3xl font-bold text-gray-800">
          <FaRupeeSign className="inline text-2xl" /> {amount}
        </p>
        <p className="text-xs text-gray-400 mt-1">{feeMonth || 'Monthly Fee'}</p>
      </motion.div>

      <div className="mb-6">
        <motion.button
          whileHover={{ scale: 1.02 }}
          whileTap={{ scale: 0.98 }}
          onClick={handlePhonePePayment}
          className="w-full p-4 rounded-xl border-2 border-purple-500 bg-purple-50 transition-all duration-300"
        >
          <div className="flex items-center gap-4">
            <div className="p-3 rounded-xl bg-gradient-to-r from-purple-600 to-blue-500 text-white">
              <FaMobileAlt className="text-2xl" />
            </div>
            <div className="flex-1 text-left">
              <p className="font-semibold text-gray-800">PhonePe UPI</p>
              <p className="text-sm text-gray-500">Pay using PhonePe UPI</p>
            </div>
            <motion.div
              animate={{ rotate: 0 }}
              transition={{ duration: 0.3 }}
            >
              <FaCheckCircle className="text-green-500 text-xl" />
            </motion.div>
          </div>
        </motion.button>
      </div>

      <div className="flex items-center justify-center gap-2 text-xs text-gray-400 mt-4">
        <FaShieldAlt className="text-green-500" />
        <span>256-bit SSL Secure</span>
        <span className="w-px h-4 bg-gray-300" />
        <span>🔒 UPI Enabled</span>
      </div>

      <button
        onClick={onCancel}
        className="w-full mt-4 py-2 text-gray-500 hover:text-gray-700 text-sm transition"
      >
        Cancel Payment
      </button>
    </motion.div>
  );
};

export default PhonePePayment;
